import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGO_LIGHT } from '../App';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL;

// Splash image URL
const SPLASH_IMAGE = '/sfondo_login.webp';
// Desktop background image
const DESKTOP_BG_IMAGE = '/sfondo_login.webp';

export default function ForgotPassword() {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
        <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80 z-[1]" />

        <div className="auth-page-content items-center" style={{ paddingBottom: '50px' }}>
          <div className="px-5 pb-4 w-full max-w-md">
            {/* Glass card */}
            <div className="backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            <div className="text-center">
              <div className="bg-green-500/20 backdrop-blur-sm rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Email Inviata!</h2>
              <p className="text-white/80 mb-4">
                Se l'indirizzo <span className="text-white font-medium">{email}</span> è registrato, 
                riceverai un'email con le istruzioni per reimpostare la password.
              </p>
              <p className="text-white/50 text-sm mb-6">
                Controlla anche la cartella spam.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 rounded-2xl font-semibold bg-[#E8334A] text-white"
                data-testid="back-to-login-btn"
              >
                Torna al Login
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
      <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/80 z-[1]" />

      <div className="auth-page-content items-center">
        <div className="px-5 pb-4 w-full max-w-md">
          {/* Glass card */}
          <div className="backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            {/* Back button */}
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Indietro</span>
            </button>

            {/* Logo */}
            <div className="text-center mb-5">
              <img 
                src={LOGO_LIGHT} 
                alt="Slimmy"
                className="h-9 mx-auto mb-1.5 drop-shadow-lg"
              />
              <p className="text-white/90 text-sm font-medium">
                Recupera la tua password
              </p>
            </div>

            <p className="text-white/70 text-sm text-center mb-5">
              Inserisci l'email associata al tuo account e ti invieremo un link per reimpostare la password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="forgot-email-input"
                  required
                />
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!email || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  email && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  'Invia Link di Reset'
                )}
              </button>
            </form>

            {/* Back to login */}
            <p className="text-white/50 text-sm mt-4 text-center">
              Ricordi la password?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-white font-medium hover:underline"
              >
                Accedi
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
